/**
 * Gestion des comptes formateurs (Scolarité).
 *
 * 3 onglets :
 *   1. Créer/Renouveler : cocher des enseignants + choix semestre + année,
 *      un clic → comptes créés/renouvelés avec mot de passe généré.
 *   2. Liste : tous les comptes formateurs avec mot de passe temporaire (pour
 *      dictée), matières enseignées, dernière connexion, statut mot de passe
 *      changé.
 *   3. Connectés : formateurs actifs récemment (< 15 min).
 *
 * Accessible aux rôles admin ET agent scolarité.
 */
import React, { useMemo, useState } from 'react';
import {
  Users, UserPlus, RefreshCw, KeyRound, Search, Filter, Loader2,
  CheckCircle2, XCircle, Eye, EyeOff, Copy, Wifi, X, AlertTriangle,
  Ban, RotateCcw, Clock, Phone, Mail, ClipboardList, FileDown, Printer,
} from 'lucide-react';
import {
  useListerComptesQuery, useCreerComptesMutation,
  useRenouvelerCompteMutation, useDesactiverCompteMutation,
  useFormateursConnectesQuery,
  type CompteFormateur, type CreerComptesResultat,
} from '../../services/comptesFormateursApi';
import { useEnseignantsDisponiblesCompteQuery } from '../../services/coreApi';
import { useGetPolesQuery } from '../../services/polesOptionsApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';

type Onglet = 'creer' | 'liste' | 'connectes';

const ComptesFormateurs: React.FC = () => {
  const [onglet, setOnglet] = useState<Onglet>('liste');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Users size={26} className="text-indigo-600" />
          Gestion des comptes formateurs
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Créez, renouvelez et suivez les comptes des formateurs par semestre.
        </p>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-6">
          {([
            { id: 'liste' as const, label: 'Liste des comptes', icon: <ClipboardList size={15} /> },
            { id: 'creer' as const, label: 'Créer / Renouveler', icon: <UserPlus size={15} /> },
            { id: 'connectes' as const, label: 'Connectés', icon: <Wifi size={15} /> },
          ]).map((t) => (
            <button
              key={t.id}
              onClick={() => setOnglet(t.id)}
              className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 transition-colors ${
                onglet === t.id
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
      </div>

      {onglet === 'liste' && <OngletListe />}
      {onglet === 'creer' && <OngletCreer />}
      {onglet === 'connectes' && <OngletConnectes />}
    </div>
  );
};

// ─── Onglet Liste ────────────────────────────────────────────────────────────

const OngletListe: React.FC = () => {
  const [semestre, setSemestre] = useState<'' | 'S1' | 'S2'>('');
  const [statut, setStatut] = useState<'all' | 'mdp_non_change' | 'mdp_change'>('all');
  const [pole, setPole] = useState('');
  const [search, setSearch] = useState('');
  const [showPassIds, setShowPassIds] = useState<Set<number>>(new Set());

  const { data: poles = [] } = useGetPolesQuery({ actif: true });
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data, isLoading, isFetching, refetch } = useListerComptesQuery({
    semestre: semestre || undefined,
    statut,
    pole: pole || undefined,
    search: search || undefined,
    annee_academique: anneeActive?.libelle,
  });

  const [renouveler, { isLoading: renouvellant }] = useRenouvelerCompteMutation();
  const [desactiver] = useDesactiverCompteMutation();

  const [renouvellementResult, setRenouvellementResult] = useState<null | {
    nom: string; mdp: string;
  }>(null);

  const togglePass = (id: number) => {
    setShowPassIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const copyClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch { /* noop */ }
  };

  const handleRenouveler = async (u: CompteFormateur) => {
    if (!window.confirm(`Générer un nouveau mot de passe pour ${u.nom_complet} ?`)) return;
    try {
      const r = await renouveler({
        userId: u.id,
        semestre: u.semestre_actif || undefined,
        annee_academique: u.annee_academique_actif || undefined,
      }).unwrap();
      setRenouvellementResult({ nom: r.nom_complet, mdp: r.nouveau_mot_de_passe });
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };

  const handleDesactiver = async (u: CompteFormateur) => {
    if (!window.confirm(`Désactiver le compte de ${u.nom_complet} ?`)) return;
    try { await desactiver(u.id).unwrap(); } catch { /* noop */ }
  };

  return (
    <div className="space-y-3">
      {/* KPI */}
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <KpiSmall label="Total comptes" value={data.total} color="indigo" />
          <KpiSmall label="MDP non changé" value={data.nb_mdp_non_change} color="amber" />
          <KpiSmall label="MDP changé" value={data.nb_mdp_change} color="emerald" />
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Semestre :</span>
          {(['', 'S1', 'S2'] as const).map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setSemestre(s)}
              className={`px-2.5 py-1 rounded text-xs font-medium ${
                semestre === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {s || 'Tous'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Pôle :</span>
          <select value={pole} onChange={(e) => setPole(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
            <option value="">Tous</option>
            {poles.map((p) => <option key={p.id} value={p.nom}>{p.nom}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Statut MDP :</span>
          <select value={statut} onChange={(e) => setStatut(e.target.value as any)}
            className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
            <option value="all">Tous</option>
            <option value="mdp_non_change">Non changé (initial)</option>
            <option value="mdp_change">Changé</option>
          </select>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher nom / username / email…"
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs font-medium rounded disabled:opacity-60">
          {isFetching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Actualiser
        </button>
        <ExportPdfButton
          semestre={semestre}
          pole={pole}
          anneeAcademique={anneeActive?.libelle || ''}
        />
      </div>

      {/* Tableau */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            <Loader2 size={16} className="inline-block animate-spin mr-1" /> Chargement…
          </div>
        ) : (data?.resultats.length || 0) === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">Aucun compte formateur.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Formateur', 'Contacts', 'Matières · Niveaux', 'Semestre', 'Mot de passe', 'MDP changé', 'Dernière connexion', 'Actions'].map((h) => (
                    <th key={h} className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data!.resultats.map((u) => (
                  <tr key={u.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${!u.actif ? 'opacity-50' : ''}`}>
                    <td className="px-2 py-2">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">{u.nom_complet}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{u.username}</div>
                    </td>
                    <td className="px-2 py-2 text-[11px]">
                      {u.telephone && (
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <Phone size={10} /> {u.telephone}
                        </div>
                      )}
                      {u.email && (
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 truncate max-w-[180px]" title={u.email}>
                          <Mail size={10} /> {u.email}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="text-gray-900 dark:text-gray-100">
                        <span className="font-semibold">{u.nb_matieres}</span> matière{u.nb_matieres > 1 ? 's' : ''}
                      </div>
                      {u.niveaux.length > 0 && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {u.niveaux.map((n) => (
                            <span key={n} className="inline-block mr-1 px-1 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">
                              {n}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {u.semestre_actif ? (
                        <>
                          <div className="font-medium">{u.semestre_actif}</div>
                          <div className="text-[10px] text-gray-500">{u.annee_academique_actif}</div>
                        </>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {u.mot_de_passe_change ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-[10px]">
                          <CheckCircle2 size={11} /> Choisi par le formateur
                        </span>
                      ) : u.mot_de_passe_temporaire ? (
                        <div className="flex items-center gap-1">
                          <code className="px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded font-mono text-[11px]">
                            {showPassIds.has(u.id) ? u.mot_de_passe_temporaire : '••••••••'}
                          </code>
                          <button onClick={() => togglePass(u.id)}
                            className="p-0.5 text-gray-400 hover:text-gray-600" title="Afficher / masquer">
                            {showPassIds.has(u.id) ? <EyeOff size={10} /> : <Eye size={10} />}
                          </button>
                          <button onClick={() => copyClipboard(u.mot_de_passe_temporaire)}
                            className="p-0.5 text-gray-400 hover:text-gray-600" title="Copier">
                            <Copy size={10} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-[10px]">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {u.mot_de_passe_change ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded text-[10px] font-semibold">
                          Oui
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded text-[10px] font-semibold">
                          Non
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-[10px] text-gray-600 dark:text-gray-400">
                      {u.derniere_connexion
                        ? new Date(u.derniere_connexion).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : <span className="italic">Jamais</span>}
                    </td>
                    <td className="px-2 py-2">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => handleRenouveler(u)}
                          disabled={renouvellant}
                          className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded disabled:opacity-50"
                          title="Renouveler le mot de passe"
                        >
                          <RotateCcw size={13} />
                        </button>
                        {u.actif && (
                          <button
                            onClick={() => handleDesactiver(u)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title="Désactiver le compte"
                          >
                            <Ban size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal résultat renouvellement */}
      {renouvellementResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setRenouvellementResult(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <KeyRound size={16} className="text-indigo-600" />
                Nouveau mot de passe généré
              </h3>
              <button onClick={() => setRenouvellementResult(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Pour <strong>{renouvellementResult.nom}</strong> :
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-center px-4 py-3 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded font-mono text-lg font-bold tracking-wider">
                  {renouvellementResult.mdp}
                </code>
                <button
                  onClick={() => copyClipboard(renouvellementResult.mdp)}
                  className="p-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded"
                  title="Copier"
                >
                  <Copy size={16} />
                </button>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 text-xs text-amber-800 dark:text-amber-200">
                <AlertTriangle size={12} className="inline mr-1" />
                Le formateur doit changer ce mot de passe à sa première connexion. Ce mot de passe restera visible dans la liste jusqu'à ce que le formateur choisisse le sien.
              </div>
              <button
                onClick={() => setRenouvellementResult(null)}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded"
              >
                J'ai noté
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Onglet Créer / Renouveler ───────────────────────────────────────────────

const OngletCreer: React.FC = () => {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [regenerer, setRegenerer] = useState(false);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [resultat, setResultat] = useState<CreerComptesResultat[] | null>(null);

  const { data: enseignants } = useEnseignantsDisponiblesCompteQuery({ search: search || undefined });
  const [creer, { isLoading: creating }] = useCreerComptesMutation();

  const filteredList = useMemo(() => enseignants?.results || [], [enseignants]);
  const toggle = (id: number) => {
    setSelection((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    if (selection.size === filteredList.length) setSelection(new Set());
    else setSelection(new Set(filteredList.map((e) => e.id)));
  };
  const selectionSansCompte = () => setSelection(new Set(filteredList.filter((e) => !e.a_compte).map((e) => e.id)));

  const handleCreer = async () => {
    if (selection.size === 0) return;
    if (!anneeActive?.libelle) {
      alert('Année académique active introuvable.');
      return;
    }
    try {
      const r = await creer({
        enseignant_ids: Array.from(selection),
        semestre,
        annee_academique: anneeActive.libelle,
        regenerer_si_existant: regenerer,
      }).unwrap();
      setResultat(r.resultats);
      setSelection(new Set());
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-3">
      {/* Barre config */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Semestre du compte</label>
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded p-0.5">
              {(['S1', 'S2'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSemestre(s)}
                  className={`px-4 py-1.5 rounded text-sm font-semibold ${
                    semestre === s ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Année académique</label>
            <div className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium">
              {anneeActive?.libelle || '—'}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={regenerer} onChange={(e) => setRegenerer(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            <span className="text-gray-700 dark:text-gray-300">Regénérer les mots de passe si le compte existe déjà</span>
          </label>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un enseignant…"
              className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <button onClick={toggleAll}
            className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded font-medium">
            {selection.size === filteredList.length ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
          <button onClick={selectionSansCompte}
            className="px-3 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 text-blue-700 dark:text-blue-300 rounded font-medium">
            Ceux sans compte
          </button>
        </div>
      </div>

      {/* Liste des enseignants */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[50vh] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
            <tr>
              <th className="px-2 py-2 w-8"></th>
              <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase">Nom</th>
              <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase">Email</th>
              <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase">Spécialité</th>
              <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase">Compte</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredList.map((e) => (
              <tr key={e.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer ${
                selection.has(e.id) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
              }`}
                onClick={() => toggle(e.id)}
              >
                <td className="px-2 py-1.5">
                  <input type="checkbox" checked={selection.has(e.id)} onChange={() => toggle(e.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    onClick={(ev) => ev.stopPropagation()} />
                </td>
                <td className="px-2 py-1.5 font-medium">{e.nom_complet}</td>
                <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{e.email || '—'}</td>
                <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{e.specialite || '—'}</td>
                <td className="px-2 py-1.5">
                  {e.a_compte ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 dark:text-blue-300">
                      <CheckCircle2 size={10} /> Existant
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-400 italic">Aucun</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bouton */}
      <div className="sticky bottom-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between shadow-lg">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <strong className="text-indigo-600 dark:text-indigo-400">{selection.size}</strong> enseignant{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}
        </div>
        <button onClick={handleCreer} disabled={creating || selection.size === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded">
          {creating ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
          {creating ? 'Création…' : `Créer / Renouveler les comptes (${selection.size})`}
        </button>
      </div>

      {/* Résultat */}
      {resultat && <ResultatCreation resultats={resultat} onClose={() => setResultat(null)} />}
    </div>
  );
};

const ResultatCreation: React.FC<{ resultats: CreerComptesResultat[]; onClose: () => void }> = ({ resultats, onClose }) => {
  const [showAll, setShowAll] = useState(false);
  const copyAll = async () => {
    const text = resultats
      .filter((r) => r.success && r.mot_de_passe_temporaire)
      .map((r) => `${r.nom_complet}\tusername: ${r.username}\tmdp: ${r.mot_de_passe_temporaire}`)
      .join('\n');
    try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
  };

  const nbCrees = resultats.filter((r) => r.compte_cree).length;
  const nbRenouv = resultats.filter((r) => r.compte_renouvele).length;
  const nbIgnores = resultats.filter((r) => r.success && !r.compte_cree && !r.compte_renouvele).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            Résultat de la création ({resultats.length} traité{resultats.length > 1 ? 's' : ''})
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-3 gap-2">
            <KpiSmall label="Créés" value={nbCrees} color="emerald" />
            <KpiSmall label="Renouvelés" value={nbRenouv} color="indigo" />
            <KpiSmall label="Ignorés" value={nbIgnores} color="gray" />
          </div>
          <div className="flex items-center justify-between">
            <button onClick={() => setShowAll((v) => !v)}
              className="text-xs text-indigo-600 hover:underline">
              {showAll ? 'Masquer les mots de passe' : 'Afficher tous les mots de passe'}
            </button>
            <button onClick={copyAll}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded">
              <Copy size={11} /> Tout copier
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-2 py-2 text-left">Nom</th>
                  <th className="px-2 py-2 text-left">Username</th>
                  <th className="px-2 py-2 text-left">Mot de passe</th>
                  <th className="px-2 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {resultats.map((r) => (
                  <tr key={r.enseignant_id} className={r.success ? '' : 'bg-red-50 dark:bg-red-900/20'}>
                    <td className="px-2 py-1.5 font-medium">{r.nom_complet || `Enseignant ${r.enseignant_id}`}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-400">{r.username || '—'}</td>
                    <td className="px-2 py-1.5">
                      {r.mot_de_passe_temporaire ? (
                        <code className="px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded font-mono">
                          {showAll ? r.mot_de_passe_temporaire : '••••••••'}
                        </code>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.compte_cree && <span className="text-emerald-700 dark:text-emerald-400">Créé</span>}
                      {r.compte_renouvele && <span className="text-indigo-700 dark:text-indigo-400">Renouvelé</span>}
                      {r.success && !r.compte_cree && !r.compte_renouvele && (
                        <span className="text-gray-500 italic">{r.message || 'Conservé'}</span>
                      )}
                      {!r.success && <span className="text-red-600">{r.message}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Onglet Connectés ────────────────────────────────────────────────────────

const OngletConnectes: React.FC = () => {
  const [minutes, setMinutes] = useState(15);
  const { data, isLoading, refetch, isFetching } = useFormateursConnectesQuery({ minutes });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Fenêtre :</span>
          {[5, 15, 30, 60].map((m) => (
            <button key={m} onClick={() => setMinutes(m)}
              className={`px-2.5 py-1 rounded text-xs font-medium ${
                minutes === m
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>
              {m} min
            </button>
          ))}
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-xs font-medium rounded disabled:opacity-60">
          {isFetching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Actualiser
        </button>
        {data && (
          <div className="ml-auto text-sm">
            <strong className="text-emerald-600 dark:text-emerald-400">{data.total_connectes}</strong> formateur{data.total_connectes > 1 ? 's' : ''} actif{data.total_connectes > 1 ? 's' : ''}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            <Loader2 size={16} className="inline-block animate-spin mr-1" /> Chargement…
          </div>
        ) : (data?.resultats.length || 0) === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            Aucun formateur connecté sur les {minutes} dernières minutes.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {data!.resultats.map((f) => {
              const ago = Math.round((Date.now() - new Date(f.derniere_connexion).getTime()) / 60000);
              return (
                <div key={f.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{f.nom_complet}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-3">
                      <span className="font-mono">{f.username}</span>
                      {f.telephone && <span className="flex items-center gap-1"><Phone size={9} /> {f.telephone}</span>}
                      {f.semestre_actif && <span className="px-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">{f.semestre_actif}</span>}
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1"><Clock size={10} /> Il y a {ago === 0 ? 'moins d\'1' : ago} min</div>
                    <div className="text-[10px]">{new Date(f.derniere_connexion).toLocaleTimeString('fr-FR')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Bouton export PDF (identifiants par pôle) ──────────────────────────────

const ExportPdfButton: React.FC<{
  semestre: string;
  pole: string;
  anneeAcademique: string;
}> = ({ semestre, pole, anneeAcademique }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inclureChange, setInclureChange] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleExport = async () => {
    setErr(null);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (semestre) params.set('semestre', semestre);
      if (pole) params.set('pole', pole);
      if (anneeAcademique) params.set('annee_academique', anneeAcademique);
      if (inclureChange) params.set('inclure_change', 'true');
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const resp = await fetch(`/api/comptes-formateurs/export-pdf/?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try { const j = await resp.json(); detail = j.detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const nameMatch = /filename="?([^"]+)"?/.exec(cd);
      const filename = nameMatch?.[1] || 'formateurs_identifiants.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      setOpen(false);
    } catch (e: any) {
      setErr(e.message || 'Erreur téléchargement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded"
        title="Exporter la liste PDF des formateurs par pôle avec leurs identifiants"
      >
        <FileDown size={12} /> Export PDF
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !loading && setOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Printer size={16} className="text-indigo-600" />
                Export PDF — identifiants formateurs
              </h3>
              <button onClick={() => setOpen(false)} disabled={loading} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Le PDF sera groupé <strong>par pôle</strong> (une section par pôle), avec pour chaque formateur : nom, téléphone, username et mot de passe temporaire.
              </p>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded p-2 text-xs text-indigo-800 dark:text-indigo-200 space-y-1">
                <div className="font-semibold">Filtres appliqués :</div>
                <div>• Année : <strong>{anneeAcademique || 'toutes'}</strong></div>
                <div>• Semestre : <strong>{semestre || 'tous'}</strong></div>
                <div>• Pôle : <strong>{pole || 'tous les pôles'}</strong></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={inclureChange} onChange={(e) => setInclureChange(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-gray-700 dark:text-gray-300">
                  Inclure aussi les formateurs qui ont déjà changé leur mot de passe
                  <span className="block text-[10px] text-gray-500">(par défaut : uniquement ceux qui doivent encore le changer, avec le mdp à dicter)</span>
                </span>
              </label>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-1.5">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                <span><strong>Document confidentiel</strong> — à remettre en main propre. Ne pas envoyer par email.</span>
              </div>
              {err && (
                <div className="text-xs bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 text-red-700 dark:text-red-300">
                  {err}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setOpen(false)} disabled={loading}
                  className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-40">
                  Annuler
                </button>
                <button onClick={handleExport} disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                  {loading ? 'Génération…' : 'Télécharger le PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Helpers UI ──────────────────────────────────────────────────────────────

const KpiSmall: React.FC<{ label: string; value: number; color: 'indigo' | 'emerald' | 'amber' | 'gray' }> = ({ label, value, color }) => {
  const bg = {
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300',
    gray: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300',
  }[color];
  return (
    <div className={`rounded border p-2 ${bg}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
};

export default ComptesFormateurs;
